// ─────────────────────────────────────────────
//  FacilityStaffingSummary — condensed staffing adequacy for ANOTHER facility,
//  shown under the Timeline page's detailed panel. Label header + the four
//  shift boxes (3/5/7/9 PM) side by side, NUMBERS ONLY:
//      Rooms: 15 · CRNA: 15 (+1 spare) · Doctor: 2 (−1)
//  Same shift colour scheme as the main panel (amber/blue/violet/red) and the
//  same maths (buildTimeline → staffing + doctorStaffing). Live by
//  construction: it runs its own rooms/roster/assignments listeners for the
//  facility, so any change on that campus's board recomputes instantly.
//
//  Anesthesia Team Control (ATC)
//  Copyright © 2026 Gonzalo Martinez (gamc00@gmail.com). All rights reserved.
// ─────────────────────────────────────────────
import React, { useEffect, useMemo, useState } from "react";
import { useRooms } from "../hooks/useRooms";
import { useLocations } from "../hooks/useLocations";
import { useDailyRoster } from "../hooks/useDailyRoster";
import { listenToAssignments } from "../firebase/firestore";
import { buildTimeline, SHIFT_LABEL_COLORS } from "../domain/timeline";
import { withDeparted } from "../domain/secondProvider";
import tokens from "../theme/tokens";

const { color, font, radius } = tokens;

// Adequacy tone for a spare-count: green surplus, amber even, red short.
const deltaTone = (delta) =>
  delta > 0 ? color.accent.active : delta === 0 ? color.accent.closing : color.accent.error;
const deltaText = (delta) => (delta > 0 ? `+${delta} spare` : delta === 0 ? "even" : `${delta}`);

export default function FacilityStaffingSummary({ facilityId, label, date }) {
  const { rooms } = useRooms(facilityId);
  const { locations } = useLocations(facilityId);
  const { roster } = useDailyRoster(facilityId, date);

  const [assignments, setAssignments] = useState([]);
  useEffect(() => {
    if (!facilityId || !date) {
      setAssignments([]);
      return undefined;
    }
    const unsub = listenToAssignments(facilityId, date, (snap) =>
      setAssignments(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
    return unsub;
  }, [facilityId, date]);

  const rosterDoctorEntries = useMemo(
    () =>
      Array.from(
        new Map(
          [
            ...(roster?.aicId ? [{ id: roster.aicId, name: roster.aicName }] : []),
            ...(roster?.supervisors || []),
          ]
            .filter((m) => m && m.id)
            .map((m) => [m.id, m])
        ).values()
      ),
    [roster]
  );

  const model = useMemo(
    () => buildTimeline(rooms, locations, assignments, withDeparted(roster?.crnas, roster?.departed), rosterDoctorEntries),
    [rooms, locations, assignments, roster, rosterDoctorEntries]
  );

  return (
    <div style={st.wrap}>
      <div style={st.label}>{label}</div>
      <div style={st.row}>
        {model.staffing.map((b, i) => {
          const doc = model.doctorStaffing[i] || {};
          const shiftColor = SHIFT_LABEL_COLORS[Math.floor(b.boundaryMin / 60)] || color.text.muted;
          return (
            <div key={b.boundaryMin} style={{ ...st.box, borderTopColor: shiftColor }}>
              <div style={{ ...st.shift, color: shiftColor }}>{b.label}</div>
              <div style={st.line}>Rooms: <strong>{b.rooms}</strong></div>
              <div style={st.line}>
                CRNA: <strong>{b.crnas}</strong>{" "}
                <span style={{ color: deltaTone(b.delta), fontWeight: font.weight.bold }}>({deltaText(b.delta)})</span>
              </div>
              <div style={st.line}>
                Doctor: <strong>{doc.doctors ?? 0}</strong>{" "}
                <span style={{ color: deltaTone(doc.delta ?? 0), fontWeight: font.weight.bold }}>({deltaText(doc.delta ?? 0)})</span>
                {(doc.soloRooms ?? 0) > 0 && <span style={{ color: "#b45309" }}> · Solo: {doc.soloRooms}</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const st = {
  wrap: { marginTop: 14 },
  label: { fontSize: font.size.label, fontWeight: font.weight.bold, color: color.text.secondary, marginBottom: 6 },
  row: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 8 },
  box: { background: color.surface.card, border: `1px solid ${color.border.default}`, borderTop: "3px solid", borderRadius: radius.sm, padding: "8px 10px" },
  shift: { fontSize: 11, fontWeight: font.weight.bold, letterSpacing: ".3px", marginBottom: 4 },
  line: { fontSize: 12, color: color.text.secondary, lineHeight: 1.5 },
};
